import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { translatePA } from '../utils/practiceAreaUtils';
import { attorneyAPI, surveyAPI, analyticsAPI, ratingAPI, savedAttorneyAPI } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { CASE_CATEGORIES } from '../constants/caseCategories';
import AttorneyCard from '../components/common/AttorneyCard';
import { displayLocation } from '../utils/locationUtils';

const SERVER_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');

const REGIONS = [
  'tashkent_city','tashkent','andijan','fergana','namangan',
  'samarkand','bukhara','navoi','kashkadarya','surkhandarya',
  'jizzakh','sirdarya','khorezm','karakalpakstan',
];
const REGION_LABELS_RU = {
  tashkent_city:'Ташкент (город)',tashkent:'Ташкентская область',
  andijan:'Андижанская область',fergana:'Ферганская область',
  namangan:'Наманганская область',samarkand:'Самаркандская область',
  bukhara:'Бухарская область',navoi:'Навоийская область',
  kashkadarya:'Кашкадарьинская область',surkhandarya:'Сурхандарьинская область',
  jizzakh:'Джизакская область',sirdarya:'Сырдарьинская область',
  khorezm:'Хорезмская область',karakalpakstan:'Республика Каракалпакстан',
};
const REGION_KEY_BY_RU = Object.fromEntries(Object.entries(REGION_LABELS_RU).map(([k,v])=>[v,k]));

// Subregions: display label (Uzbek) + value stored/searched (Russian)
// Reverse lookup: Russian subregion value → Uzbek label (for display when selected)
const SUBREGIONS = {
  // Tashkent city has no districts in this picker — just the single "Ташкент (город)" region.
  tashkent_city: [],
  tashkent: [
    { label: 'Нурафшон шаҳри',      value: 'г. Нурафшон' },
    { label: 'Чирчиқ шаҳри',        value: 'г. Чирчик' },
    { label: 'Олмалиқ шаҳри',       value: 'г. Алмалык' },
    { label: 'Ангрен шаҳри',        value: 'г. Ангрен' },
    { label: 'Бекобод шаҳри',       value: 'г. Бекабад' },
    { label: 'Янгийўл шаҳри',       value: 'г. Янгиюль' },
    { label: 'Зангиота тумани',     value: 'Зангиатинский район' },
    { label: 'Қибрай тумани',       value: 'Кибрайский район' },
    { label: 'Бўстонлиқ тумани',    value: 'Бостанлыкский район' },
    { label: 'Бўка тумани',         value: 'Букинский район' },
    { label: 'Паркент тумани',      value: 'Паркентский район' },
    { label: 'Пскент тумани',       value: 'Пскентский район' },
    { label: 'Тошкент тумани',      value: 'Ташкентский район' },
    { label: 'Ўртачирчиқ тумани',   value: 'Уртачирчикский район' },
    { label: 'Юқоричирчиқ тумани',  value: 'Юкоричирчикский район' },
    { label: 'Оҳангарон шаҳри',     value: 'г. Охангарон' },
    { label: 'Чиноз тумани',        value: 'Чиназский район' },
    { label: 'Оққўрғон тумани',     value: 'Аккурганский район' },
    { label: 'Қуйи Чирчиқ тумани', value: 'Куйичирчикский район' },
  ],
  andijan: [
    { label: 'Андижон шаҳри',        value: 'г. Андижан' },
    { label: 'Андижон тумани',       value: 'Андижанский район' },
    { label: 'Асака тумани',         value: 'Асакинский район' },
    { label: 'Балиқчи тумани',       value: 'Балыкчинский район' },
    { label: 'Булоқбоши тумани',     value: 'Булакбашский район' },
    { label: 'Бўстон тумани',        value: 'Бустанский район' },
    { label: 'Жалақудуқ тумани',     value: 'Джалалкудукский район' },
    { label: 'Избоскан тумани',      value: 'Избасканский район' },
    { label: 'Мархамат тумани',      value: 'Мархаматский район' },
    { label: 'Олтинкўл тумани',      value: 'Алтыкульский район' },
    { label: 'Пахтаобод тумани',     value: 'Пахтаабадский район' },
    { label: 'Қўрғонтепа тумани',    value: 'Кургантепинский район' },
    { label: 'Улуғнор тумани',       value: 'Улугнорский район' },
    { label: 'Хонобод тумани',       value: 'Ханабадский район' },
    { label: 'Хўжаобод тумани',      value: 'Ходжаабадский район' },
    { label: 'Шаҳрихон тумани',      value: 'Шахриханский район' },
  ],
  fergana: [
    { label: 'Фарғона шаҳри',       value: 'г. Фергана' },
    { label: 'Қўқон шаҳри',         value: 'г. Коканд' },
    { label: 'Марғилон шаҳри',      value: 'г. Маргилан' },
    { label: 'Қувасой шаҳри',       value: 'г. Кувасай' },
    { label: 'Қува тумани',         value: 'Кувинский район' },
    { label: 'Риштон тумани',       value: 'Риштанский район' },
    { label: 'Бағдод тумани',       value: 'Багдадский район' },
    { label: 'Бешариқ тумани',      value: 'Бешарыкский район' },
    { label: 'Бувайда тумани',       value: 'Бувайдинский район' },
    { label: 'Данғара тумани',       value: 'Дангаринский район' },
    { label: 'Қуштепа тумани',       value: 'Куштепинский район' },
    { label: 'Сўх тумани',           value: 'Сохский район' },
    { label: 'Тошлоқ тумани',        value: 'Ташлакский район' },
    { label: 'Фурқат тумани',        value: 'Фуркатский район' },
    { label: 'Ёзёвон тумани',        value: 'Язяванский район' },
  ],
  namangan: [
    { label: 'Наманган шаҳри',        value: 'г. Наманган' },
    { label: 'Чуст тумани',          value: 'Чустский район' },
    { label: 'Поп тумани',           value: 'Папский район' },
    { label: 'Косонсой тумани',      value: 'Касансайский район' },
    { label: 'Давлатобод тумани',    value: 'Давлатабадский район' },
    { label: 'Мингбулоқ тумани',     value: 'Мингбулакский район' },
    { label: 'Норин тумани',         value: 'Норинский район' },
    { label: 'Тўрақўрғон тумани',    value: 'Туракурганский район' },
    { label: 'Уйчи тумани',          value: 'Уйчинский район' },
    { label: 'Учқўрғон тумани',      value: 'Учкурганский район' },
    { label: 'Янгиқўрғон тумани',    value: 'Янгикурганский район' },
    { label: 'Янги Наманган тумани', value: 'Янги-Наманганский район' },
    { label: 'Чортоқ тумани',        value: 'Чартакский район' },
  ],
  samarkand: [
    { label: 'Самарқанд шаҳри',     value: 'г. Самарканд' },
    { label: 'Каттақўрғон шаҳри',   value: 'г. Каттакурган' },
    { label: 'Ургут тумани',        value: 'Ургутский район' },
    { label: 'Пайариқ тумани',      value: 'Пайарыкский район' },
    { label: 'Иштихон тумани',      value: 'Иштиханский район' },
    { label: 'Жомбой тумани',       value: 'Джамбайский район' },
    { label: 'Оқдарё тумани',       value: 'Акдарьинский район' },
    { label: 'Булунғур тумани',     value: 'Булунгурский район' },
    { label: 'Қўшрабод тумани',     value: 'Кошрабадский район' },
    { label: 'Нарпай тумани',       value: 'Нарпайский район' },
    { label: 'Нуробод тумани',      value: 'Нурабадский район' },
    { label: 'Пастдарғом тумани',   value: 'Пастдаргомский район' },
    { label: 'Пахтачи тумани',      value: 'Пахтачийский район' },
    { label: 'Тайлоқ тумани',       value: 'Тайлакский район' },
  ],
  bukhara: [
    { label: 'Бухоро шаҳри',        value: 'г. Бухара' },
    { label: 'Когон тумани',         value: 'г. Каган' },
    { label: 'Ромитан тумани',      value: 'Ромитанский район' },
    { label: 'Қоракўл тумани',      value: 'Каракульский район' },
    { label: 'Ғиждувон тумани',     value: 'Гиждуванский район' },
    { label: 'Вобкент тумани',      value: 'Вабкентский район' },
    { label: 'Жондор тумани',       value: 'Джандарский район' },
    { label: 'Қоровулбозор тумани', value: 'Каравулбазарский район' },
    { label: 'Олот тумани',         value: 'Алатский район' },
    { label: 'Пешку тумани',        value: 'Пешкунский район' },
    { label: 'Шофиркон тумани',     value: 'Шафирканский район' },
  ],
  navoi: [
    { label: 'Навоий шаҳри',        value: 'г. Навои' },
    { label: 'Зарафшон шаҳри',      value: 'г. Зарафшан' },
    { label: 'Учқудуқ тумани',      value: 'Учкудукский район' },
    { label: 'Нурота тумани',       value: 'Нуратинский район' },
    { label: 'Ғозғон шаҳри',        value: 'Газганский район' },
    { label: 'Конимех тумани',      value: 'Конимехский район' },
    { label: 'Кармана тумани',      value: 'Карманинский район' },
    { label: 'Қизилтепа тумани',    value: 'Кызылтепинский район' },
    { label: 'Навбаҳор тумани',     value: 'Навбахорский район' },
    { label: 'Томди тумани',        value: 'Томдинский район' },
    { label: 'Хатирчи тумани',      value: 'Хатырчинский район' },
  ],
  kashkadarya: [
    { label: 'Қарши шаҳри',         value: 'г. Карши' },
    { label: 'Шаҳрисабз шаҳри',     value: 'г. Шахрисабз' },
    { label: 'Қарши тумани',        value: 'Каршинский район' },
    { label: 'Китоб тумани',        value: 'Китабский район' },
    { label: 'Кўкдала тумани',      value: 'Кукдалинский район' },
    { label: 'Косон тумани',        value: 'Касанский район' },
    { label: 'Муборак тумани',      value: 'Мубарекский район' },
    { label: 'Миришкор тумани',     value: 'Миришкорский район' },
    { label: 'Ғузор тумани',        value: 'Гузарский район' },
    { label: 'Деҳқонобод тумани',   value: 'Дехканабадский район' },
    { label: 'Қамаши тумани',       value: 'Камашинский район' },
    { label: 'Қасби тумани',        value: 'Касбинский район' },
    { label: 'Нишон тумани',        value: 'Нишанский район' },
    { label: 'Чироқчи тумани',      value: 'Чиракчинский район' },
    { label: 'Яккабоғ тумани',      value: 'Яккабагский район' },
  ],
  surkhandarya: [
    { label: 'Термиз шаҳри',        value: 'г. Термез' },
    { label: 'Ангор тумани',        value: 'Ангорский район' },
    { label: 'Денов тумани',        value: 'Денауский район' },
    { label: 'Шеробод тумани',      value: 'Шерабадский район' },
    { label: 'Бойсун тумани',       value: 'Байсунский район' },
    { label: 'Бандихон тумани',     value: 'Бандиханский район' },
    { label: 'Жарқўрғон тумани',    value: 'Джаркурганский район' },
    { label: 'Қизириқ тумани',      value: 'Кизирикский район' },
    { label: 'Қумқўрғон тумани',    value: 'Кумкурганский район' },
    { label: 'Музработ тумани',     value: 'Музрабадский район' },
    { label: 'Олтинсой тумани',     value: 'Алтынсайский район' },
    { label: 'Сариосиё тумани',     value: 'Сариасийский район' },
    { label: 'Узун тумани',         value: 'Узунский район' },
    { label: 'Шўрчи тумани',        value: 'Шурчинский район' },
  ],
  jizzakh: [
    { label: 'Жиззах шаҳри',        value: 'г. Джизак' },
    { label: 'Зомин тумани',        value: 'Заминский район' },
    { label: 'Ғаллаорол тумани',    value: 'Галляаральский район' },
    { label: 'Янгиобод тумани',     value: 'Янгиабадский район' },
    { label: 'Арнасой тумани',      value: 'Арнасайский район' },
    { label: 'Бахмал тумани',       value: 'Бахмалский район' },
    { label: 'Дўстлик тумани',      value: 'Дустликский район' },
    { label: 'Зарбдор тумани',      value: 'Зарбдарский район' },
    { label: 'Зафаробод тумани',    value: 'Зафарабадский район' },
    { label: 'Мирзачўл тумани',     value: 'Мирзачульский район' },
    { label: 'Пахтакор тумани',     value: 'Пахтакорский район' },
    { label: 'Фориш тумани',        value: 'Фаришский район' },
    { label: 'Шароф Рашидов тумани',value: 'Шараф-Рашидовский район' },
  ],
  sirdarya: [
    { label: 'Гулистон шаҳри',      value: 'г. Гулистан' },
    { label: 'Янгиер шаҳри',        value: 'г. Янгиер' },
    { label: 'Хавос тумани',        value: 'Хавастский район' },
    { label: 'Сардоба тумани',      value: 'Сардобинский район' },
    { label: 'Оқолтин тумани',      value: 'Акалтынский район' },
    { label: 'Боёвут тумани',       value: 'Баяутский район' },
    { label: 'Мирзаобод тумани',    value: 'Мирзаабадский район' },
    { label: 'Сайхунобод тумани',   value: 'Сайхунабадский район' },
    { label: 'Ширин шаҳри',         value: 'г. Ширин' },
    { label: 'Сирдарё тумани',      value: 'Сырдарьинский район' },
  ],
  khorezm: [
    { label: 'Урганч шаҳри',        value: 'г. Ургенч' },
    { label: 'Хива шаҳри',          value: 'г. Хива' },
    { label: 'Питнак шаҳри',        value: 'г. Питнак' },
    { label: 'Хонқа тумани',        value: 'Ханкинский район' },
    { label: 'Шовот тумани',        value: 'Шаватский район' },
    { label: 'Боғот тумани',        value: 'Багатский район' },
    { label: 'Гурлан тумани',       value: 'Гурленский район' },
    { label: 'Қўшкўпир тумани',     value: 'Кушкупырский район' },
    { label: 'Хазорасп тумани',     value: 'Хазараспский район' },
    { label: 'Янгиариқ тумани',     value: 'Янгиарыкский район' },
    { label: 'Янгибозор тумани',    value: 'Янгибазарский район' },
  ],
  karakalpakstan: [
    { label: 'Нукус шаҳри',         value: 'г. Нукус' },
    { label: 'Тахиатош шаҳри',      value: 'г. Тахиаташ' },
    { label: 'Тўрткўл шаҳри',       value: 'г. Турткуль' },
    { label: 'Хўжайли шаҳри',       value: 'г. Ходжейли' },
    { label: 'Чимбой шаҳри',        value: 'г. Чимбай' },
    { label: 'Беруний тумани',      value: 'Берунийский район' },
    { label: 'Қўнғирот тумани',     value: 'Кунградский район' },
    { label: 'Амударё тумани',      value: 'Амударьинский район' },
    { label: 'Кегейли тумани',      value: 'Кегейлийский район' },
    { label: 'Қонликўл тумани',     value: 'Канликольский район' },
    { label: 'Мўйноқ тумани',       value: 'Муйнакский район' },
    { label: 'Тахиатош тумани',     value: 'Тахиатошский район' },
    { label: 'Тахтакўпир тумани',   value: 'Тахтакупырский район' },
    { label: 'Шўманай тумани',      value: 'Шуманайский район' },
    { label: 'Элликқалъа тумани',   value: 'Элликкалинский район' },
  ],
};

const SUBREGION_VALUE_TO_LABEL = {};
Object.values(SUBREGIONS).forEach(subs => subs.forEach(sub => { SUBREGION_VALUE_TO_LABEL[sub.value] = sub.label; }));

// Context so FilterOption can close the dropdown regardless of nesting depth
const FilterCloseCtx = React.createContext(null);

// Custom styled dropdown (matches navbar style)
const FilterDropdown = ({ label, value, onClear, children }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  return (
    <div ref={ref} style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '0.75rem 1rem', border: '1px solid #e2e8f0', borderRadius: '8px',
          backgroundColor: 'white', fontSize: '1rem', cursor: 'pointer', color: value ? '#2d3748' : '#a0aec0',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0 }}>
          {value && (
            <span
              onClick={e => { e.stopPropagation(); onClear(); }}
              style={{ fontSize: '1rem', color: '#a0aec0', lineHeight: 1 }}
            >×</span>
          )}
          <svg width="10" height="6" viewBox="0 0 10 6" style={{ opacity: 0.5 }}>
            <path d="M1 1l4 4 4-4" stroke="#718096" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </button>
      {open && (
        <FilterCloseCtx.Provider value={() => setOpen(false)}>
          <div style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 1000,
            backgroundColor: 'white', borderRadius: '10px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.14)', border: '1px solid #e2e8f0',
            maxHeight: '260px', overflowY: 'auto', padding: '0.4rem 0',
          }}>
            {children}
          </div>
        </FilterCloseCtx.Provider>
      )}
    </div>
  );
};

const FilterOption = ({ onClick, active, children }) => {
  const onClose = React.useContext(FilterCloseCtx);
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={() => { onClick(); onClose?.(); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: '0.6rem 1rem', fontSize: '0.95rem', cursor: 'pointer',
        color: active ? '#2b6cb0' : '#2d3748',
        fontWeight: active ? '600' : '400',
        backgroundColor: hover ? '#f7fafc' : 'transparent',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </div>
  );
};

const LocationDropdown = ({ filters, setFilters, fetchWithFilters, t }) => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const ref = useRef(null);
  const { language } = useLanguage();

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const selectedLabel = filters.location
    ? (REGION_KEY_BY_RU[filters.location]
        ? t(`regions.${REGION_KEY_BY_RU[filters.location]}`)
        : language === 'ru'
          ? filters.location
          : (SUBREGION_VALUE_TO_LABEL[filters.location] || filters.location))
    : t('findAttorney.locationPlaceholder');

  const select = (value) => {
    const f = { ...filters, location: value };
    setFilters(f);
    fetchWithFilters(f);
    setOpen(false);
  };

  const toggleExpand = (key, e) => {
    e.stopPropagation();
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div ref={ref} style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '0.75rem 1rem', border: '1px solid #e2e8f0', borderRadius: '8px',
          backgroundColor: 'white', fontSize: '1rem', cursor: 'pointer',
          color: filters.location ? '#2d3748' : '#a0aec0',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedLabel}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0 }}>
          {filters.location && (
            <span onClick={e => { e.stopPropagation(); select(''); }} style={{ fontSize: '1rem', color: '#a0aec0', lineHeight: 1 }}>×</span>
          )}
          <svg width="10" height="6" viewBox="0 0 10 6" style={{ opacity: 0.5 }}>
            <path d="M1 1l4 4 4-4" stroke="#718096" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 1000,
          backgroundColor: 'white', borderRadius: '10px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.14)', border: '1px solid #e2e8f0',
          maxHeight: '320px', overflowY: 'auto', padding: '0.4rem 0',
        }}>
          {/* All regions option */}
          <LocationOption active={!filters.location} onClick={() => select('')}>
            {t('findAttorney.locationPlaceholder')}
          </LocationOption>

          {REGIONS.map(key => {
            const subs = SUBREGIONS[key] || [];
            const hasSubs = subs.length > 0;
            const isExpanded = expanded[key];
            const regionActive = filters.location === REGION_LABELS_RU[key];
            const anySubActive = subs.some(s => filters.location === s.value);
            return (
              <React.Fragment key={key}>
                {/* Region row — click toggles subregions, or selects directly if there are none */}
                <div
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '0.6rem 1rem', cursor: 'pointer', fontSize: '0.95rem',
                    color: (regionActive || anySubActive) ? '#2b6cb0' : '#2d3748',
                    fontWeight: (regionActive || anySubActive) ? '600' : '400',
                  }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f7fafc'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                  onClick={(e) => hasSubs ? toggleExpand(key, e) : select(REGION_LABELS_RU[key])}
                >
                  <span>{t(`regions.${key}`)}</span>
                  {hasSubs && (
                    <svg width="10" height="6" viewBox="0 0 10 6" style={{ opacity: 0.5, flexShrink: 0, transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
                      <path d="M1 1l4 4 4-4" stroke="#718096" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>

                {/* Subregions — first item is "барчаси" for the parent region */}
                {hasSubs && isExpanded && (
                  <>
                    <div
                      onClick={() => select(REGION_LABELS_RU[key])}
                      style={{
                        padding: '0.45rem 1rem 0.45rem 1.75rem', cursor: 'pointer',
                        fontSize: '0.88rem',
                        color: regionActive ? '#2b6cb0' : '#4a5568',
                        fontWeight: regionActive ? '600' : '400',
                        backgroundColor: 'transparent',
                      }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f7fafc'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      {t(`regions.${key}`)} {language === 'ru' ? '(все)' : '(барчаси)'}
                    </div>
                    {subs.map(sub => (
                      <div
                        key={sub.value}
                        onClick={() => select(sub.value)}
                        style={{
                          padding: '0.45rem 1rem 0.45rem 1.75rem', cursor: 'pointer',
                          fontSize: '0.88rem',
                          color: filters.location === sub.value ? '#2b6cb0' : '#4a5568',
                          fontWeight: filters.location === sub.value ? '600' : '400',
                          backgroundColor: 'transparent',
                        }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f7fafc'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        {language === 'ru' ? sub.value : sub.label}
                      </div>
                    ))}
                  </>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Free-text name search with debounced autocomplete suggestions (matches Cyrillic or Latin input).
const NameSearchInput = ({ filters, setFilters, fetchWithFilters, t, language }) => {
  const [inputValue, setInputValue] = useState(filters.search || '');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    setInputValue(filters.search || '');
  }, [filters.search]);

  const applySearch = (val) => {
    const f = { ...filters, search: val };
    setFilters(f);
    fetchWithFilters(f);
    setOpen(false);
  };

  const onChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.trim().length < 2) { setSuggestions([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await attorneyAPI.suggest(val.trim());
        setSuggestions(res.data || []);
        setOpen(true);
      } catch { setSuggestions([]); }
    }, 250);
  };

  const selectSuggestion = (attorney) => {
    setInputValue(attorney.full_name);
    setSuggestions([]);
    applySearch(attorney.full_name);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); applySearch(inputValue.trim()); }
  };

  const clear = () => { setInputValue(''); setSuggestions([]); applySearch(''); };

  return (
    <div ref={ref} style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
      <div style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: '0.4rem',
        padding: '0.75rem 1rem', border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: 'white',
      }}>
        <input
          type="text"
          value={inputValue}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder={t('findAttorney.searchPlaceholder')}
          style={{ flex: 1, border: 'none', outline: 'none', fontSize: '1rem', color: '#2d3748', minWidth: 0, backgroundColor: 'transparent' }}
        />
        {inputValue && (
          <span onClick={clear} style={{ fontSize: '1rem', color: '#a0aec0', lineHeight: 1, cursor: 'pointer', flexShrink: 0 }}>×</span>
        )}
      </div>
      {open && suggestions.length > 0 && (
        <div className="name-suggest-dropdown" style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 1000,
          backgroundColor: 'white', borderRadius: '10px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.14)', border: '1px solid #e2e8f0',
          maxHeight: '320px', overflowY: 'auto', padding: '0.4rem',
          scrollbarWidth: 'none', msOverflowStyle: 'none',
        }}>
          <style>{`.name-suggest-dropdown::-webkit-scrollbar { display: none; }`}</style>
          {suggestions.map((s, i) => (
            <div
              key={s.id}
              onClick={() => selectSuggestion(s)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.65rem',
                padding: '0.5rem', borderRadius: '8px', cursor: 'pointer',
                borderBottom: i < suggestions.length - 1 ? '1px solid #f1f5f9' : 'none',
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f7fafc'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <div style={{
                width: '38px', height: '38px', borderRadius: '50%', overflow: 'hidden',
                backgroundColor: '#e8edf2', display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#a0aec0', flexShrink: 0,
              }}>
                {s.profile_picture ? (
                  <img src={`${SERVER_URL}${s.profile_picture}`} alt={s.full_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                )}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '0.92rem', fontWeight: '600', color: '#2d3748', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.full_name}
                </div>
                <div style={{ fontSize: '0.78rem', color: '#718096', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.location ? displayLocation(s.location, t, language) : t('findAttorney.noLocationSpecified')}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const LocationOption = ({ active, onClick, children }) => {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: '0.6rem 1rem', fontSize: '0.95rem', cursor: 'pointer',
        color: active ? '#2b6cb0' : '#2d3748',
        fontWeight: active ? '600' : '400',
        backgroundColor: hover ? '#f7fafc' : 'transparent',
      }}
    >
      {children}
    </div>
  );
};

// Geographic coordinates (lat, lon) for each region capital
const REGION_COORDS = {
  tashkent_city:  [41.299, 69.240],
  tashkent:       [41.100, 69.800],
  andijan:        [40.783, 72.344],
  fergana:        [40.374, 71.787],
  namangan:       [41.004, 71.642],
  samarkand:      [39.654, 66.975],
  bukhara:        [39.768, 64.455],
  navoi:          [40.084, 65.380],
  kashkadarya:    [38.860, 65.988],
  surkhandarya:   [37.935, 67.612],
  jizzakh:        [40.122, 67.843],
  sirdarya:       [40.837, 68.675],
  khorezm:        [41.523, 60.361],
  karakalpakstan: [42.461, 59.618],
};

// Find the nearest region labels (by straight-line coordinate distance) for a searched string
const findNearbyRegionLabels = (searchedLabel, topN = 3) => {
  if (!searchedLabel) return [];
  const norm = searchedLabel.toLowerCase().trim();

  // Match searched string to a known region key
  const matchedKey = Object.entries(REGION_LABELS_RU).find(([, label]) => {
    const l = label.toLowerCase();
    return l === norm || l.startsWith(norm.split(' ')[0]) || norm.startsWith(l.split(' ')[0]);
  })?.[0];

  if (!matchedKey || !REGION_COORDS[matchedKey]) return [];

  const [lat, lon] = REGION_COORDS[matchedKey];

  return Object.entries(REGION_COORDS)
    .filter(([key]) => key !== matchedKey)
    .map(([key, [rlat, rlon]]) => ({
      label: REGION_LABELS_RU[key],
      dist: Math.sqrt((lat - rlat) ** 2 + (lon - rlon) ** 2),
    }))
    .sort((a, b) => a.dist - b.dist)
    .slice(0, topN)
    .map(d => d.label);
};

const useWindowWidth = () => {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return w;
};

const SURVEY_QUESTION_KEY = 'Адвокат топа олдингизми?';

const NO_REASON_OPTIONS = [
  { key: 'no_specialization', ru: 'Не нашёл нужной специализации',              uz: 'Керакли мутахассислик топилмади' },
  { key: 'no_region',         ru: 'Не нашёл адвоката в моём регионе',           uz: 'Менинг ҳудудимда адвокат топа олмадим' },
  { key: 'no_contact',        ru: 'Не смог связаться с адвокатом',              uz: 'Адвокат билан боғлана олмадим' },
  { key: 'no_info',           ru: 'Мало информации о специалистах',             uz: 'Мутахассислар ҳақида маълумот кам' },
  { key: 'specific_not_found',ru: 'Искал определенного адвоката, но не нашёл', uz: 'Маълум бир адвокатни қидирдим, лекин топа олмадим' },
  { key: 'other',             ru: 'Другое',                                     uz: 'Бошқа' },
];

const NoReasonPopup = ({ language, selected, onSelect, onSubmit, onClose }) => {
  const isRu = language === 'ru';
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9000,
      backgroundColor: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem',
    }}>
      <div style={{
        backgroundColor: 'white', borderRadius: '16px',
        padding: '2rem', maxWidth: '440px', width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1a365d', marginBottom: '0.5rem' }}>
          {isRu ? 'Почему вам не удалось найти адвоката?' : 'Нега адвокат топа олмадингиз?'}
        </h3>
        <p style={{ fontSize: '0.88rem', color: '#718096', marginBottom: '1.25rem' }}>
          {isRu
            ? 'Пожалуйста, выберите причину — это поможет нам сделать сервис лучше.'
            : 'Илтимос, сабабни танланг — бу хизматимизни яхшилашга ёрдам беради.'}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
          {NO_REASON_OPTIONS.map(opt => (
            <button
              key={opt.key}
              onClick={() => onSelect(opt.key)}
              style={{
                textAlign: 'left', padding: '0.65rem 1rem', borderRadius: '8px', cursor: 'pointer',
                fontSize: '0.92rem', fontWeight: selected === opt.key ? '600' : '400',
                border: `1.5px solid ${selected === opt.key ? '#3182ce' : '#e2e8f0'}`,
                backgroundColor: selected === opt.key ? '#ebf8ff' : 'white',
                color: selected === opt.key ? '#2b6cb0' : '#2d3748',
                transition: 'all 0.12s',
              }}
            >
              {isRu ? opt.ru : opt.uz}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{ padding: '0.6rem 1.25rem', border: '1px solid #e2e8f0', borderRadius: '8px', background: 'white', color: '#718096', cursor: 'pointer', fontSize: '0.9rem' }}
          >
            {isRu ? 'Пропустить' : 'Ўтказиб юбориш'}
          </button>
          <button
            onClick={onSubmit}
            disabled={!selected}
            style={{
              padding: '0.6rem 1.5rem', border: 'none', borderRadius: '8px', cursor: selected ? 'pointer' : 'not-allowed',
              backgroundColor: selected ? '#1a365d' : '#e2e8f0', color: selected ? 'white' : '#a0aec0',
              fontSize: '0.9rem', fontWeight: '600', transition: 'background-color 0.12s',
            }}
          >
            {isRu ? 'Отправить' : 'Юбориш'}
          </button>
        </div>
      </div>
    </div>
  );
};

const RegionPopup = ({ language, t, selectedRegion, onSelect, onSkip, onSubmit }) => {
  const isRu = language === 'ru';
  const [expanded, setExpanded] = useState({});
  const toggleExpand = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9100,
      backgroundColor: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem',
    }}>
      <div style={{
        backgroundColor: 'white', borderRadius: '16px',
        padding: '2rem', maxWidth: '440px', width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        display: 'flex', flexDirection: 'column', maxHeight: '85vh',
      }}>
        <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1a365d', marginBottom: '0.5rem' }}>
          {isRu ? 'В каком регионе вы искали адвоката?' : 'Қайси ҳудудда адвокат қидирдингиз?'}
        </h3>
        <p style={{ fontSize: '0.88rem', color: '#718096', marginBottom: '1rem' }}>
          {isRu
            ? 'Выберите регион (или район) — это поможет нам подключить адвокатов в этом регионе.'
            : 'Ҳудудни (ёки туманни) танланг — бу бизга шу ҳудудда адвокатларни жалб қилишга ёрдам беради.'}
        </p>
        <div style={{ overflowY: 'auto', marginBottom: '1.25rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
          {REGIONS.map(key => {
            const subs = SUBREGIONS[key] || [];
            const hasSubs = subs.length > 0;
            const isExpanded = expanded[key];
            const regionActive = selectedRegion === REGION_LABELS_RU[key];
            const anySubActive = subs.some(s => selectedRegion === s.value);
            return (
              <React.Fragment key={key}>
                <div
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '0.6rem 1rem', cursor: 'pointer', fontSize: '0.92rem',
                    color: (regionActive || anySubActive) ? '#2b6cb0' : '#2d3748',
                    fontWeight: (regionActive || anySubActive) ? '600' : '400',
                    borderBottom: '1px solid #f1f5f9',
                  }}
                  onClick={() => hasSubs ? toggleExpand(key) : onSelect(REGION_LABELS_RU[key])}
                >
                  <span>{t(`regions.${key}`)}</span>
                  {hasSubs && (
                    <svg width="10" height="6" viewBox="0 0 10 6" style={{ opacity: 0.5, flexShrink: 0, transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
                      <path d="M1 1l4 4 4-4" stroke="#718096" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
                {hasSubs && isExpanded && (
                  <>
                    <div
                      onClick={() => onSelect(REGION_LABELS_RU[key])}
                      style={{
                        padding: '0.45rem 1rem 0.45rem 1.75rem', cursor: 'pointer', fontSize: '0.86rem',
                        color: regionActive ? '#2b6cb0' : '#4a5568',
                        fontWeight: regionActive ? '600' : '400',
                        borderBottom: '1px solid #f1f5f9',
                      }}
                    >
                      {t(`regions.${key}`)} {isRu ? '(весь регион)' : '(барчаси)'}
                    </div>
                    {subs.map(sub => (
                      <div
                        key={sub.value}
                        onClick={() => onSelect(sub.value)}
                        style={{
                          padding: '0.45rem 1rem 0.45rem 1.75rem', cursor: 'pointer', fontSize: '0.86rem',
                          color: selectedRegion === sub.value ? '#2b6cb0' : '#4a5568',
                          fontWeight: selectedRegion === sub.value ? '600' : '400',
                          borderBottom: '1px solid #f1f5f9',
                        }}
                      >
                        {isRu ? sub.value : sub.label}
                      </div>
                    ))}
                  </>
                )}
              </React.Fragment>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <button
            onClick={onSkip}
            style={{ padding: '0.6rem 1.25rem', border: '1px solid #e2e8f0', borderRadius: '8px', background: 'white', color: '#718096', cursor: 'pointer', fontSize: '0.9rem' }}
          >
            {isRu ? 'Пропустить' : 'Ўтказиб юбориш'}
          </button>
          <button
            onClick={onSubmit}
            disabled={!selectedRegion}
            style={{
              padding: '0.6rem 1.5rem', border: 'none', borderRadius: '8px', cursor: selectedRegion ? 'pointer' : 'not-allowed',
              backgroundColor: selectedRegion ? '#1a365d' : '#e2e8f0', color: selectedRegion ? 'white' : '#a0aec0',
              fontSize: '0.9rem', fontWeight: '600', transition: 'background-color 0.12s',
            }}
          >
            {isRu ? 'Отправить' : 'Юбориш'}
          </button>
        </div>
      </div>
    </div>
  );
};

const FindAttorney = () => {
  const { t, language } = useLanguage();
  const { user, isAttorney, isClient } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [attorneys, setAttorneys] = useState([]);
  const [nearbyAttorneys, setNearbyAttorneys] = useState([]);
  const [nearbyRegions, setNearbyRegions] = useState([]);
  const [searchedLocation, setSearchedLocation] = useState('');
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    practiceArea: location.state?.practiceArea || searchParams.get('practiceArea') || '',
    location: location.state?.location || searchParams.get('location') || '',
    search: location.state?.search || searchParams.get('search') || '',
  });
  const [surveyAnswered, setSurveyAnswered] = useState(false);
  const [showNoReasonPopup, setShowNoReasonPopup] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [showRegionPopup, setShowRegionPopup] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [ratings, setRatings] = useState({});
  const [savedIds, setSavedIds] = useState(new Set());
  const showSurvey = !isAttorney;
  const isMobile = useWindowWidth() < 768;

  useEffect(() => {
    if (!isClient) return;
    savedAttorneyAPI.listIds().then(res => setSavedIds(new Set(res.data || []))).catch(() => {});
  }, [isClient]);

  const fetchRatings = async (list) => {
    if (!list.length) return;
    try {
      const res = await ratingAPI.bulk(list.map(a => a.id));
      setRatings(prev => ({ ...prev, ...res.data }));
    } catch {}
  };

  const fetchWithFilters = async (f) => {
    if (f.practiceArea || f.location || f.search) {
      analyticsAPI.searchClick(f.practiceArea, f.location, f.search);
    }
    setLoading(true);
    setNearbyAttorneys([]);
    setNearbyRegions([]);
    try {
      const params = {};
      if (f.practiceArea) params.practiceArea = f.practiceArea;
      if (f.location) params.location = f.location;
      if (f.search) params.search = f.search;

      const response = await attorneyAPI.getAll(params);
      const results = response.data || [];
      setAttorneys(results);
      setSearchedLocation(f.location || '');
      fetchRatings(results);

      // No results + location filter → look for attorneys in nearby regions
      if (results.length === 0 && f.location) {
        const nearbyLabels = findNearbyRegionLabels(f.location);
        if (nearbyLabels.length > 0) {
          const nearbyResponses = await Promise.all(
            nearbyLabels.map(loc => attorneyAPI.getAll({ location: loc }).catch(() => ({ data: [] })))
          );
          const allNearby = nearbyResponses
            .flatMap(r => r.data || [])
            .filter((a, i, arr) => arr.findIndex(b => b.id === a.id) === i);
          setNearbyAttorneys(allNearby);
          fetchRatings(allNearby);
          setNearbyRegions(
            nearbyLabels.filter((loc, i) => (nearbyResponses[i]?.data || []).length > 0)
          );
        }
      }
    } catch (error) {
      console.error('Error fetching attorneys:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const newFilters = {
      practiceArea: location.state?.practiceArea || searchParams.get('practiceArea') || '',
      location: location.state?.location || searchParams.get('location') || '',
      search: location.state?.search || searchParams.get('search') || '',
    };
    setFilters(newFilters);
    fetchWithFilters(newFilters);
  }, [location.key]);

  const fetchAttorneys = () => fetchWithFilters(filters);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchAttorneys();
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>{t('findAttorney.title')}</h1>
        <p style={styles.subtitle}>{t('findAttorney.subtitle')}</p>
      </div>

      <form style={styles.filters} onSubmit={handleSearch}>
        {/* Name search with autocomplete */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', flex: 1, minWidth: '200px' }}>
          <label style={styles.filterLabel}>{t('findAttorney.search')}</label>
          <NameSearchInput filters={filters} setFilters={setFilters} fetchWithFilters={fetchWithFilters} t={t} language={language} />
        </div>

        {/* Practice area custom dropdown */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', flex: 1, minWidth: '200px' }}>
          <label style={styles.filterLabel}>{t('findAttorney.practiceArea')}</label>
          <FilterDropdown
            label={filters.practiceArea ? translatePA(filters.practiceArea, language) : t('findAttorney.allAreas')}
            value={filters.practiceArea}
            onClear={() => { const f = { ...filters, practiceArea: '' }; setFilters(f); fetchWithFilters(f); }}
          >
            <FilterOption active={!filters.practiceArea} onClick={() => { const f = { ...filters, practiceArea: '' }; setFilters(f); fetchWithFilters(f); }}>
              {t('findAttorney.allAreas')}
            </FilterOption>
            {CASE_CATEGORIES.map((cat, ci) => (
              <React.Fragment key={ci}>
                <div style={{ padding: '0.35rem 1rem', fontSize: '0.75rem', fontWeight: '700', color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: ci > 0 ? '0.25rem' : 0 }}>
                  {translatePA(cat.label, language)}
                </div>
                {(cat.subs.length > 0 ? cat.subs : [cat.label]).map(val => (
                  <FilterOption
                    key={val}
                    active={filters.practiceArea === val}
                    onClick={() => { const f = { ...filters, practiceArea: val }; setFilters(f); fetchWithFilters(f); }}
                  >
                    {translatePA(val, language)}
                  </FilterOption>
                ))}
              </React.Fragment>
            ))}
          </FilterDropdown>
        </div>

        {/* Location custom dropdown */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', flex: 1, minWidth: '200px' }}>
          <label style={styles.filterLabel}>{t('findAttorney.locationLabel')}</label>
          <LocationDropdown
            filters={filters}
            setFilters={setFilters}
            fetchWithFilters={fetchWithFilters}
            t={t}
          />
        </div>
      </form>

      {loading ? (
        <div style={styles.loading}>{t('findAttorney.loading')}</div>
      ) : attorneys.length > 0 ? (
        <div style={styles.grid}>
          {attorneys.map(attorney => (
            <AttorneyCard key={attorney.id} attorney={attorney} rating={ratings[attorney.id]} saved={savedIds.has(attorney.id)} />
          ))}
        </div>
      ) : nearbyAttorneys.length > 0 ? (
        <>
          <div style={styles.noResultsBox}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#c05621" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <div>
              <strong>В регионе «{searchedLocation}» адвокатов не найдено.</strong>
              <span style={{ color: '#744210' }}> Ближайшие адвокаты из: {nearbyRegions.join(', ')}</span>
            </div>
          </div>
          <div style={styles.grid}>
            {nearbyAttorneys.map(attorney => (
              <AttorneyCard key={attorney.id} attorney={attorney} rating={ratings[attorney.id]} saved={savedIds.has(attorney.id)} />
            ))}
          </div>
        </>
      ) : (
        <div style={styles.emptyState}>
          <h3 style={{ marginBottom: '0.5rem', color: '#4a5568' }}>{t('findAttorney.noAttorneysFound')}</h3>
          <p style={{ color: '#718096' }}>{t('findAttorney.adjustSearch')}</p>
        </div>
      )}

      {/* No-reason popup */}
      {showNoReasonPopup && (
        <NoReasonPopup
          language={language}
          selected={selectedReason}
          onSelect={setSelectedReason}
          onSubmit={async () => {
            if (!selectedReason) return;
            if (selectedReason === 'no_region') {
              setShowNoReasonPopup(false);
              setShowRegionPopup(true);
              return;
            }
            try { await surveyAPI.submitNoReason(selectedReason); } catch (_) {}
            setShowNoReasonPopup(false);
            setSurveyAnswered(true);
          }}
          onClose={() => { setShowNoReasonPopup(false); setSurveyAnswered(true); }}
        />
      )}

      {/* Region popup — shown when "no_region" reason is selected */}
      {showRegionPopup && (
        <RegionPopup
          language={language}
          t={t}
          selectedRegion={selectedRegion}
          onSelect={setSelectedRegion}
          onSkip={async () => {
            try { await surveyAPI.submitNoReason('no_region'); } catch (_) {}
            setShowRegionPopup(false);
            setSurveyAnswered(true);
          }}
          onSubmit={async () => {
            if (!selectedRegion) return;
            try { await surveyAPI.submitNoReason('no_region', selectedRegion); } catch (_) {}
            setShowRegionPopup(false);
            setSurveyAnswered(true);
          }}
        />
      )}

      {/* Survey — shown to guests and clients, not attorneys */}
      {showSurvey && !loading && (
        <div style={{
          marginTop: '3rem',
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '1.5rem 2rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: isMobile ? 'center' : 'space-between',
          flexWrap: 'wrap',
          gap: '1rem',
          border: '1px solid #e2e8f0',
          textAlign: isMobile ? 'center' : 'left',
        }}>
          {surveyAnswered ? (
            <p style={{ color: '#276749', fontWeight: '500', margin: 0 }}>{t('survey.thanks')}</p>
          ) : (
            <>
              <p style={{ margin: 0, fontWeight: '500', color: '#2d3748', fontSize: '1rem' }}>
                {t('survey.question')}
              </p>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                {[{ labelKey: 'survey.yes', value: true }, { labelKey: 'survey.no', value: false }].map(({ labelKey, value }) => (
                  <button
                    key={labelKey}
                    onClick={async () => {
                      try { await surveyAPI.submit(SURVEY_QUESTION_KEY, value); } catch (_) {}
                      if (value === false) {
                        setShowNoReasonPopup(true);
                      } else {
                        setSurveyAnswered(true);
                      }
                    }}
                    style={{
                      padding: '0.5rem 1.5rem',
                      borderRadius: '6px',
                      border: '1px solid #e2e8f0',
                      backgroundColor: 'white',
                      color: '#2d3748',
                      fontWeight: '600',
                      fontSize: '0.95rem',
                      cursor: 'pointer',
                    }}
                    onMouseOver={e => { e.currentTarget.style.backgroundColor = '#FFD700'; e.currentTarget.style.color = '#0d1f17'; e.currentTarget.style.borderColor = '#FFD700'; }}
                    onMouseOut={e => { e.currentTarget.style.backgroundColor = 'white'; e.currentTarget.style.color = '#2d3748'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                  >
                    {t(labelKey)}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '2rem',
  },
  header: {
    marginBottom: '2rem',
  },
  title: {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#1a365d',
    marginBottom: '0.5rem',
  },
  subtitle: {
    color: '#718096',
    fontSize: '1rem',
  },
  filters: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    marginBottom: '1.5rem',
    display: 'flex',
    gap: '1rem',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
  },
  filterGroup: {
    flex: '1',
    minWidth: '200px',
  },
  filterLabel: {
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: '500',
    color: '#4a5568',
    marginBottom: '0.5rem',
  },
  input: {
    width: '100%',
    padding: '0.75rem 1rem',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '1rem',
    outline: 'none',
  },
  select: {
    width: '100%',
    padding: '0.75rem 1rem',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '1rem',
    outline: 'none',
    backgroundColor: 'white',
  },
  searchButton: {
    backgroundColor: '#3182ce',
    color: 'white',
    padding: '0.75rem 1.5rem',
    border: 'none',
    borderRadius: '8px',
    fontWeight: '600',
    cursor: 'pointer',
    height: '44px',
  },
  grid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  noResultsBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    backgroundColor: '#fffaf0',
    border: '1px solid #f6ad55',
    borderRadius: '10px',
    padding: '0.9rem 1.25rem',
    marginBottom: '1.25rem',
    fontSize: '0.92rem',
    color: '#c05621',
  },
  emptyState: {
    textAlign: 'center',
    padding: '3rem',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  loading: {
    textAlign: 'center',
    padding: '3rem',
    color: '#718096',
  },
};

export default FindAttorney;
